import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Authoring Layout values for Modal. Legacy `fullscreen-side` still resolves
 * at runtime but is not offered in the catalog.
 */
export const MODAL_VARIANTS = ['default', 'fullscreen', 'sheet'] as const;

/**
 * HEN Modal Content variants using HEN Design System tokens.
 * Controls the maximum width and corner radius of the modal dialog.
 */
export const modalContentVariants = cva('', {
  variants: {
    variant: {
      default: '',
      fullscreen: 'max-w-none',
      /** Desktop sheet width when Width is unset (31.25rem / 500px). */
      sheet: 'max-w-modal-sheet',
      /** @deprecated Use `sheet`. Kept so published configs still resolve. */
      'fullscreen-side': 'max-w-modal-sheet',
    },
    size: {
      sm: 'max-w-sm',
      md: 'max-w-lg',
      lg: 'max-w-2xl',
      wide: 'max-w-modal-wide',
      xl: 'max-w-4xl',
      full: 'max-w-[calc(100vw-2rem)]',
    },
    rounded: {
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
    },
    shadow: {
      none: 'shadow-none',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

export type ModalContentVariantProps = VariantProps<
  typeof modalContentVariants
>;

/** Inner spacing around slotted `content`, below the header row. */
export const modalBodyVariants = cva('', {
  variants: {
    padding: {
      none: '',
      sm: 'p-sm',
      md: 'px-md pt-xs pb-md',
      lg: 'p-lg',
      inset: 'px-2xl py-sm',
    },
  },
  defaultVariants: {
    padding: 'none',
  },
});

export type ModalBodyVariantProps = VariantProps<typeof modalBodyVariants>;

/**
 * Side spacing on the sticky title row (title + close). Unset keeps `md`.
 * Top and bottom stay `md` at every size so the title never moves vertically.
 */
export const modalHeaderVariants = cva('', {
  variants: {
    padding: {
      none: 'py-md',
      sm: 'px-sm py-md',
      md: 'p-md',
      lg: 'px-lg py-md',
    },
  },
  defaultVariants: {
    padding: 'md',
  },
});

export type ModalHeaderVariantProps = VariantProps<typeof modalHeaderVariants>;

/** Top-corner radius of the mobile bottom sheet; unset keeps the drawer's `lg`. */
export const modalSheetVariants = cva('', {
  variants: {
    rounded: {
      md: 'data-[vaul-drawer-direction=bottom]:rounded-t-md',
      lg: 'data-[vaul-drawer-direction=bottom]:rounded-t-lg',
      xl: 'data-[vaul-drawer-direction=bottom]:rounded-t-xl',
    },
  },
});
